import { useMemo, useState } from "react";

import type { SortingState } from "@tanstack/react-table";

import type { DataTableColumnFilter } from "./DataTable.types";

export type LocalDataTableColumn<TData> = {
  value: (row: TData) => unknown;
  searchable?: boolean;
  sortValue?: (row: TData) => unknown;
};

export type UseLocalDataTableOptions<TData> = {
  rows: TData[];
  columns: Record<string, LocalDataTableColumn<TData>>;
  initialSorting?: SortingState;
  predicate?: (row: TData) => boolean;
};

export type UseLocalDataTable<TData> = {
  rows: TData[];
  total: number;
  search: string;
  setSearch: (search: string) => void;
  sorting: SortingState;
  onSortingChange: (sorting: SortingState) => void;
  columnFilters: DataTableColumnFilter[];
  onColumnFiltersChange: (filters: DataTableColumnFilter[]) => void;
};

const EMPTY_SORTING: SortingState = [];

export function useLocalDataTable<TData>({
  rows,
  columns,
  initialSorting = EMPTY_SORTING,
  predicate,
}: UseLocalDataTableOptions<TData>): UseLocalDataTable<TData> {
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [columnFilters, setColumnFilters] = useState<DataTableColumnFilter[]>(
    [],
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();

    const kept = undefined === predicate ? rows : rows.filter(predicate);

    const searched =
      "" === term
        ? kept
        : kept.filter((row) =>
            Object.values(columns).some(
              (column) =>
                false !== column.searchable &&
                text(column.value(row)).includes(term),
            ),
          );

    const filtered = searched.filter((row) =>
      columnFilters.every((filter) => {
        const column = columns[filter.id];

        return undefined === column ? true : matches(column.value(row), filter);
      }),
    );

    return sort(filtered, columns, sorting);
  }, [rows, columns, predicate, search, columnFilters, sorting]);

  return {
    rows: visible,
    total: visible.length,
    search,
    setSearch,
    sorting,
    onSortingChange: setSorting,
    columnFilters,
    onColumnFiltersChange: setColumnFilters,
  };
}

function sort<TData>(
  rows: TData[],
  columns: Record<string, LocalDataTableColumn<TData>>,
  sorting: SortingState,
): TData[] {
  const [order] = sorting;
  const column = undefined === order ? undefined : columns[order.id];

  if (undefined === order || undefined === column) {
    return rows;
  }

  const read = column.sortValue ?? column.value;
  const direction = order.desc ? -1 : 1;

  // Rows the column cannot separate keep the order they came in, which a
  // second sort on the same column would otherwise shuffle.
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const result = compare(read(a.row), read(b.row));

      return result ? direction * result : a.index - b.index;
    })
    .map(({ row }) => row);
}

function text(raw: unknown): string {
  return String(raw ?? "").toLowerCase();
}

function compare(a: unknown, b: unknown): number {
  const numbers = [a, b].map(Number);

  if (numbers.every((number) => Number.isFinite(number))) {
    return numbers[0] - numbers[1];
  }

  return String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

/** The operators the server applies, applied to one value in memory. */
function matches(raw: unknown, filter: DataTableColumnFilter): boolean {
  if ("in" === filter.operator) {
    const values = filter.values ?? [];

    return 0 === values.length || values.includes(String(raw ?? ""));
  }

  const value = filter.value;

  if (undefined === value || "" === value) {
    return true;
  }

  switch (filter.operator) {
    case "contains":
      return text(raw).includes(text(value));

    case "startsWith":
      return text(raw).startsWith(text(value));

    case "equals":
      return text(raw) === text(value);

    case "eq":
      return 0 === compare(raw, value);

    case "ne":
      return 0 !== compare(raw, value);

    case "gt":
      return compare(raw, value) > 0;

    case "lt":
      return compare(raw, value) < 0;

    case "between":
      return (
        undefined === filter.value2 ||
        "" === filter.value2 ||
        (compare(raw, value) >= 0 && compare(raw, filter.value2) <= 0)
      );

    default:
      return true;
  }
}
